import { z } from 'zod';
import { McpKeyIdentityContext } from '@sleed/bizcommerce-api';
import type { McpAvailableToolToken, McpKeyCreateRequest, McpKeyToken, McpKeyUpdateRequest } from '@sleed/bizcommerce-api';

/**
 * MCP key mint/edit form. Mirrors `McpKeyCreateRequest` plus the streamlined
 * "create service user inline" flow (docs/ai/02b §4.3: bind Admin keys to a dedicated
 * least-privilege service User, never a human account).
 *
 * Safety rails ported from Core/server/Tools/mcp/mint-mcp-key.ps1:
 * - a Destructive tool can never be granted with requiresApproval=false (the UI locks the
 *   switch, the schema rejects it, and formToCreateRequest normalizes it — belt & suspenders;
 *   the server gate forces approval for Destructive tools un-waivably regardless);
 * - Sensitive grants with requiresApproval=false are allowed but the dialog warns.
 */
export const mcpKeyGrantSchema = z.object({
	toolName: z.string().min(1),
	requiresApproval: z.boolean()
});

export type McpKeyGrantValues = z.infer<typeof mcpKeyGrantSchema>;

const serviceUserSchema = z.object({
	eml: z.string().trim().min(1, 'validation.required').max(255, 'validation.tooLong').email('validation.email'),
	psw: z.string().min(8, 'validation.passwordMin'),
	fn: z.string().trim().max(50, 'validation.tooLong'),
	ln: z.string().trim().max(50, 'validation.tooLong'),
	rid: z.number().int().positive('validation.required')
});

export type McpServiceUserValues = z.infer<typeof serviceUserSchema>;

export const mcpKeySchema = z
	.object({
		id: z.number().optional(),
		name: z.string().trim().min(1, 'validation.required').max(200, 'validation.tooLong'),
		context: z.union([z.literal(McpKeyIdentityContext.FrontEnd), z.literal(McpKeyIdentityContext.Admin)]),
		/** Admin context only: bind to an existing user or create a dedicated service user inline. */
		backingMode: z.enum(['existing', 'create']),
		backingUserId: z.number().nullable(),
		backingCustomerId: z.number().nullable(),
		siteId: z.number().nullable(),
		serviceUser: serviceUserSchema.nullable(),
		tools: z.array(mcpKeyGrantSchema)
	})
	.superRefine((v, ctx) => {
		if (v.tools.length === 0) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tools'], message: 'validation.required' });
		}
		// Backing identity is immutable after mint — pairing rules apply on create only.
		if (v.id) return;
		if (v.context === McpKeyIdentityContext.Admin) {
			if (v.backingMode === 'existing' && !(v.backingUserId && v.backingUserId > 0)) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['backingUserId'], message: 'validation.required' });
			}
			if (v.backingMode === 'create' && !v.serviceUser) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serviceUser'], message: 'validation.required' });
			}
		} else if (!(v.backingCustomerId && v.backingCustomerId > 0)) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['backingCustomerId'], message: 'validation.required' });
		}
	});

export type McpKeyFormValues = z.infer<typeof mcpKeySchema>;

export const EMPTY_MCP_KEY: McpKeyFormValues = {
	name: '',
	context: McpKeyIdentityContext.Admin,
	backingMode: 'existing',
	backingUserId: null,
	backingCustomerId: null,
	siteId: null,
	serviceUser: null,
	tools: []
};

export const EMPTY_SERVICE_USER: McpServiceUserValues = { eml: '', psw: '', fn: '', ln: '', rid: 0 };

/** Risk level strings as serialized by `McpKeysController.AvailableTools`. */
export type McpRiskLevel = 'Safe' | 'Sensitive' | 'Destructive';

/**
 * The FrontEnd (customer-context) tool set. `AvailableTools` only lists the ADMIN registry
 * (docs/ai/06 §3: admin keys see 21 tools, FrontEnd keys 9), so the FrontEnd grant picker uses
 * this constant — names + risk verified against Core/server/Libraries/BizWeb.NC.AI/Tools/FrontEnd/*.
 * The server remains the authority: unknown names are rejected at mint time per context registry.
 * The cart/checkout tools are only registered when Ai:Assistant:CartToolsEnabled is true (default false),
 * so granting them on a store without that switch is rejected on save.
 */
export const FRONTEND_TOOLS: McpAvailableToolToken[] = [
	{ name: 'get_cart', riskLevel: 'Safe', requiredPermissions: [] },
	{ name: 'get_my_orders', riskLevel: 'Safe', requiredPermissions: [] },
	{ name: 'get_order_detail', riskLevel: 'Safe', requiredPermissions: [] },
	{ name: 'compose_cart_totals', riskLevel: 'Safe', requiredPermissions: [] },
	{ name: 'compose_order_totals', riskLevel: 'Safe', requiredPermissions: [] },
	{ name: 'add_to_cart', riskLevel: 'Sensitive', requiredPermissions: [] },
	{ name: 'update_cart_item', riskLevel: 'Sensitive', requiredPermissions: [] },
	{ name: 'remove_cart_item', riskLevel: 'Sensitive', requiredPermissions: [] },
	{ name: 'place_order', riskLevel: 'Sensitive', requiredPermissions: [] }
];

/** Grants that violate the un-waivable Destructive rail (must be empty to submit). */
export function destructiveViolations(tools: McpKeyGrantValues[], riskByTool: Record<string, string | undefined>): string[] {
	return tools.filter(g => riskByTool[g.toolName] === 'Destructive' && !g.requiresApproval).map(g => g.toolName);
}

/** Sensitive grants minting without approval — allowed, but the dialog surfaces a warning. */
export function sensitiveWaivers(tools: McpKeyGrantValues[], riskByTool: Record<string, string | undefined>): string[] {
	return tools.filter(g => riskByTool[g.toolName] === 'Sensitive' && !g.requiresApproval).map(g => g.toolName);
}

/**
 * Permission name (tool `requiredPermissions`) → `UserRoleToken` flag. Same mapping as
 * ~/lib/auth/use-has-access.ts (ported from the legacy admin user_session.js).
 */
export const PERMISSION_TO_ROLE_FLAG: Record<string, string> = {
	CMS: 'perm_cms',
	Catalog: 'perm_clg',
	Customers: 'perm_cust',
	Orders: 'perm_ord',
	OrderEntry: 'perm_oe',
	OrderApproval: 'perm_oa',
	Bookings: 'perm_bookings',
	System: 'perm_sys',
	Reports: 'perm_rep',
	Pricing: 'perm_prc',
	PromotionPolicies: 'perm_prompol',
	Dashboard: 'perm_dashboard'
};

/**
 * Effective authority = backing role permissions ∩ tool requirements (docs/ai/02b §3).
 * Returns the required permissions the given role does NOT grant, so the dialog can warn
 * that granted tools would be denied at the gate.
 */
export function missingRolePermissions(
	selectedToolNames: string[],
	toolsMeta: McpAvailableToolToken[],
	roleFlags: Record<string, unknown> | null | undefined
): string[] {
	if (!roleFlags) return [];
	const required = new Set<string>();
	for (const meta of toolsMeta) {
		if (!meta.name || !selectedToolNames.includes(meta.name)) continue;
		for (const perm of meta.requiredPermissions ?? []) required.add(perm);
	}
	return [...required].filter(perm => {
		const flag = PERMISSION_TO_ROLE_FLAG[perm];
		// Unknown permission names (e.g. plain role names like FrontEndUsers) can't be checked — skip.
		return flag ? roleFlags[flag] !== true : false;
	});
}

export function formToCreateRequest(form: McpKeyFormValues, riskByTool: Record<string, string | undefined>): McpKeyCreateRequest {
	return {
		name: form.name.trim(),
		context: form.context,
		backingUserId: form.context === McpKeyIdentityContext.Admin ? form.backingUserId : null,
		backingCustomerId: form.context === McpKeyIdentityContext.FrontEnd ? form.backingCustomerId : null,
		siteId: form.context === McpKeyIdentityContext.FrontEnd ? form.siteId : null,
		tools: normalizeGrants(form.tools, riskByTool)
	};
}

export function formToUpdateRequest(form: McpKeyFormValues, riskByTool: Record<string, string | undefined>): McpKeyUpdateRequest {
	return { name: form.name.trim(), tools: normalizeGrants(form.tools, riskByTool) };
}

/** Final normalization: a Destructive grant is always minted with requiresApproval=true. */
function normalizeGrants(tools: McpKeyGrantValues[], riskByTool: Record<string, string | undefined>): McpKeyGrantValues[] {
	return tools.map(g => (riskByTool[g.toolName] === 'Destructive' ? { ...g, requiresApproval: true } : { ...g }));
}

export function tokenToForm(token: McpKeyToken): McpKeyFormValues {
	return {
		id: token.id,
		name: token.name ?? '',
		context: token.context === McpKeyIdentityContext.Admin ? McpKeyIdentityContext.Admin : McpKeyIdentityContext.FrontEnd,
		backingMode: 'existing',
		backingUserId: token.backingUserId ?? null,
		backingCustomerId: token.backingCustomerId ?? null,
		siteId: token.siteId ?? null,
		serviceUser: null,
		tools: (token.tools ?? []).map(g => ({ toolName: g.toolName ?? '', requiresApproval: g.requiresApproval ?? true }))
	};
}
